import {
  dokumentDisplayNumber,
  getDokumentTypLabel,
  isHeroOriginDokument,
  isStuecklisteListItem,
  isStundennachweisListItem,
  type DokumentListItem,
  type DokumentTyp,
} from "@/lib/dokumente";

/** Native App-Dokumente der Projektübersicht – ohne Hero-Spiegel und Stundennachweise. */
export function isNativeAppOverviewDocument(
  doc: Pick<DokumentListItem, "quelle" | "dokument_typ" | "hero_document_id">,
): boolean {
  return !isHeroOriginDokument(doc) && !isStundennachweisListItem(doc);
}

export type AppDocumentGroupKey =
  | "angebot"
  | "auftragsbestaetigung"
  | "stueckliste"
  | "lieferschein"
  | "rechnung"
  | "sonstige";

export const APP_DOCUMENT_GROUP_ORDER: AppDocumentGroupKey[] = [
  "angebot",
  "auftragsbestaetigung",
  "stueckliste",
  "lieferschein",
  "rechnung",
  "sonstige",
];

export const APP_DOCUMENT_GROUP_LABELS: Record<AppDocumentGroupKey, string> = {
  angebot: "Angebote",
  auftragsbestaetigung: "Auftragsbestätigungen",
  stueckliste: "Stücklisten",
  lieferschein: "Lieferscheine",
  rechnung: "Rechnungen",
  sonstige: "Weitere Dokumente",
};

export interface CollapsedAppDocument {
  doc: DokumentListItem;
  olderCount: number;
}

function stuecklisteFamilyKey(doc: DokumentListItem): string {
  const nummer = String(doc.dokument_nummer || "").trim();
  if (nummer) return `sl-nr:${nummer.toLowerCase()}`;
  const base = Number(doc.base_dokument_id || 0);
  if (base > 0) return `sl:${base}`;
  const slId = Number(doc.stueckliste_id || 0);
  if (slId > 0) return `sl-id:${slId}`;
  return `sl-row:${doc.id}`;
}

function isDraftStatus(doc: DokumentListItem): boolean {
  const status = String(doc.status || "").toLowerCase();
  return status === "entwurf" || status === "draft";
}

function revisionRank(doc: DokumentListItem): number {
  const revision = Number(doc.revision_index || 0);
  const slId = Number(doc.stueckliste_id || 0);
  return slId > 0 ? slId : Number(doc.id || 0) + revision * 1_000_000;
}

function isNewerStueckliste(candidate: DokumentListItem, current: DokumentListItem): boolean {
  const candidateDraft = isDraftStatus(candidate) ? 1 : 0;
  const currentDraft = isDraftStatus(current) ? 1 : 0;
  if (candidateDraft !== currentDraft) return candidateDraft > currentDraft;
  const candidateRev = Number(candidate.revision_index || 0);
  const currentRev = Number(current.revision_index || 0);
  if (candidateRev !== currentRev) return candidateRev > currentRev;
  return revisionRank(candidate) > revisionRank(current);
}

/** Stücklisten: pro Nummer/Basis nur die aktuelle Version. Andere Typen unverändert. */
export function collapseAppDocumentRevisions(docs: DokumentListItem[]): CollapsedAppDocument[] {
  const latest = new Map<string, CollapsedAppDocument>();
  const passthrough: CollapsedAppDocument[] = [];

  for (const doc of docs) {
    if (!isStuecklisteListItem(doc)) {
      passthrough.push({ doc, olderCount: 0 });
      continue;
    }
    const key = stuecklisteFamilyKey(doc);
    const current = latest.get(key);
    if (!current) {
      latest.set(key, { doc, olderCount: 0 });
      continue;
    }
    if (isNewerStueckliste(doc, current.doc)) {
      latest.set(key, { doc, olderCount: current.olderCount + 1 });
    } else {
      current.olderCount += 1;
    }
  }

  return [...passthrough, ...latest.values()].sort((a, b) => {
    const at = String(a.doc.created_at || a.doc.updated_at || "");
    const bt = String(b.doc.created_at || b.doc.updated_at || "");
    if (at !== bt) return bt.localeCompare(at);
    return Number(b.doc.id || 0) - Number(a.doc.id || 0);
  });
}

export function appDocumentGroupKey(doc: DokumentListItem): AppDocumentGroupKey {
  const typ = String(doc.dokument_typ || "") as DokumentTyp;
  if (typ === "angebot") return "angebot";
  if (typ === "auftragsbestaetigung") return "auftragsbestaetigung";
  if (typ === "stueckliste" || isStuecklisteListItem(doc)) return "stueckliste";
  if (typ === "lieferschein") return "lieferschein";
  if (typ === "rechnung") return "rechnung";
  return "sonstige";
}

export function groupCollapsedAppDocuments(items: CollapsedAppDocument[]): {
  key: AppDocumentGroupKey;
  label: string;
  items: CollapsedAppDocument[];
}[] {
  const buckets = new Map<AppDocumentGroupKey, CollapsedAppDocument[]>();
  for (const item of items) {
    const key = appDocumentGroupKey(item.doc);
    const list = buckets.get(key) || [];
    list.push(item);
    buckets.set(key, list);
  }
  return APP_DOCUMENT_GROUP_ORDER.filter((key) => (buckets.get(key) || []).length > 0).map((key) => ({
    key,
    label: APP_DOCUMENT_GROUP_LABELS[key],
    items: buckets.get(key) || [],
  }));
}

export function appDocumentRowTitle(doc: DokumentListItem): string {
  return dokumentDisplayNumber(doc);
}

export function appDocumentRowSubtitle(doc: DokumentListItem, olderCount: number): string {
  const parts = [getDokumentTypLabel(doc)];
  const betreff = String(doc.betreff || "").replace(/<[^>]+>/g, "").trim();
  if (betreff && betreff.toLowerCase() !== parts[0]?.toLowerCase()) {
    parts.push(betreff);
  }
  if (olderCount > 0) {
    parts.push(olderCount === 1 ? "1 ältere Version" : `${olderCount} ältere Versionen`);
  }
  return parts.filter(Boolean).join(" · ");
}
